import { useState, useEffect, FormEvent } from 'react';
import { AppUser } from '../../../types';
import { useAuth } from '../../../hooks/useAuth';
import { ONBOARDING_ZONES } from '../../../config/onboardingData';

export const useEditProfileForm = (appUser: AppUser, isOpen: boolean, onClose: () => void) => {
  const { updateAppUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editableFields, setEditableFields] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [formData, setFormData] = useState({
    nombreCompleto: appUser.nombreCompleto || appUser.displayName || '',
    celular: appUser.celular || '',
    tipoIdentificacion: appUser.tipoIdentificacion || 'Cédula de Ciudadanía',
    numeroIdentificacion: appUser.numeroIdentificacion || '',
    email: appUser.email || '',
    commercializationCities: appUser.commercializationCities || [],
    commercializationZones: (appUser.commercializationZones || {}) as Record<string, string[]>,
  });

  useEffect(() => {
    if (!isOpen) return;
    setEditableFields({});
    setError(null);
    setSuccess(false);
    setIsConfirmOpen(false);
    setFormData({
      nombreCompleto: appUser.nombreCompleto || appUser.displayName || '',
      celular: appUser.celular || '',
      tipoIdentificacion: appUser.tipoIdentificacion || 'Cédula de Ciudadanía',
      numeroIdentificacion: appUser.numeroIdentificacion || '',
      email: appUser.email || '',
      commercializationCities: appUser.commercializationCities || [],
      commercializationZones: (appUser.commercializationZones || {}) as Record<string, string[]>,
    });
  }, [isOpen, appUser]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleToggleEditField = (field: string) => {
    setEditableFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleCloseEditField = (field: string) => {
    setEditableFields((prev) => ({ ...prev, [field]: false }));
  };

  const handleToggleCity = (city: string) => {
    setFormData((prev) => {
      const exists = prev.commercializationCities.includes(city);
      const updatedCities = exists
        ? prev.commercializationCities.filter((c) => c !== city)
        : [...prev.commercializationCities, city];
      const updatedZones = { ...prev.commercializationZones };
      if (exists) delete updatedZones[city];
      return { ...prev, commercializationCities: updatedCities, commercializationZones: updatedZones };
    });
  };

  const handleToggleZone = (city: string, zone: string) => {
    setFormData((prev) => {
      const current = prev.commercializationZones[city] || [];
      const updated = current.includes(zone) ? current.filter((z) => z !== zone) : [...current, zone];
      return { ...prev, commercializationZones: { ...prev.commercializationZones, [city]: updated } };
    });
  };

  const handleSelectAllZones = (city: string) => {
    const allZones = ONBOARDING_ZONES[city]?.zones || [];
    setFormData((prev) => ({
      ...prev,
      commercializationZones: { ...prev.commercializationZones, [city]: [...allZones] },
    }));
  };

  const handleClearZones = (city: string) => {
    setFormData((prev) => ({
      ...prev,
      commercializationZones: { ...prev.commercializationZones, [city]: [] },
    }));
  };

  const handleRequestSave = (e: FormEvent) => {
    e.preventDefault();
    if (!formData.nombreCompleto.trim()) {
      setError('El nombre completo es obligatorio.');
      return;
    }
    setError(null);
    setIsConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const { email: _email, ...updateData } = formData;
      const targetUid = appUser.uid || appUser.id;
      if (!targetUid) throw new Error('No user id');
      await updateAppUserProfile(targetUid, {
        ...updateData,
        displayName: formData.nombreCompleto,
      });
      setIsConfirmOpen(false);
      setSuccess(true);
      setEditableFields({});
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1200);
    } catch {
      setIsConfirmOpen(false);
      setError('Error al guardar los datos de perfil.');
    } finally {
      setLoading(false);
    }
  };

  const isAnyFieldEditing = Object.values(editableFields).some(Boolean);

  return {
    formData,
    loading,
    error,
    success,
    isConfirmOpen,
    setIsConfirmOpen,
    editableFields,
    isAnyFieldEditing,
    handleFieldChange,
    handleToggleEditField,
    handleCloseEditField,
    handleToggleCity,
    handleToggleZone,
    handleSelectAllZones,
    handleClearZones,
    handleRequestSave,
    handleConfirmSave,
  };
};
